import { cloneElement, isValidElement, type ReactNode } from 'react';
import { COD_FORM_DEFAULT_WORDS, type CodFormDefaultWords } from '@xshop/shared';

// Every word the one shared cash-on-delivery card prints, in each language a store may be served in, and
// gathered in one place so a theme replaces them without a second form being built. The merchant's own
// field labels are not here: those come from the COD form config and belong to the shop.

/** The card's own wording, one key per string it draws. */
export type CheckoutLabels = {
  heading: string;
  loading: string;
  invalidFields: string;
  submitFailed: string;
  submitting: string;
  codNoticeTitle: string;
  codNoticeText: string;
  /** `{count}` is replaced by the line quantity and `{item}` by `item` or `items`. */
  subtotal: string;
  item: string;
  items: string;
  shipping: string;
  shippingPending: string;
  shippingFree: string;
  total: string;
  /** The messages under a field; `{field}` is its label and `{min}` the shortest answer it takes. */
  fieldRequired: string;
  fieldTooShort: string;
  fieldPhone: string;
  fieldChoose: string;
};

/** The direction a theme declares — the language signal for a store that has not chosen one. */
export type CheckoutDirection = 'ltr' | 'rtl';

/** The languages the card is written in: the ones a store may choose to be served in. */
export type CheckoutLanguage = 'ar' | 'fr' | 'en';

/** What the card prints where a theme gave no wording of its own — the catalog's own English. */
export const DEFAULT_CHECKOUT_LABELS: CheckoutLabels = {
  heading: 'Complete your order',
  loading: 'Loading the form…',
  invalidFields: 'Please check the highlighted fields and try again.',
  submitFailed: 'The order could not be placed. Please try again.',
  submitting: 'Placing your order…',
  codNoticeTitle: 'Cash on delivery',
  codNoticeText: 'Pay the courier after you receive and check your parcel.',
  subtotal: 'Subtotal ({count} {item})',
  item: 'item',
  items: 'items',
  shipping: 'Delivery',
  shippingPending: 'Calculating…',
  shippingFree: 'FREE',
  total: 'Total to pay',
  fieldRequired: '{field} is required',
  fieldTooShort: '{field}: at least {min} characters',
  fieldPhone: 'Enter a valid phone number, e.g. 06 12 34 56 78',
  fieldChoose: 'Choose one of the options for {field}'
};

/** The same card in French, the other language this market shops in. */
const FRENCH_CHECKOUT_LABELS: CheckoutLabels = {
  heading: 'Finalisez votre commande',
  loading: 'Chargement du formulaire…',
  invalidFields: 'Vérifiez les champs signalés puis réessayez.',
  submitFailed: 'La commande n’a pas pu être passée. Veuillez réessayer.',
  submitting: 'Envoi de votre commande…',
  codNoticeTitle: 'Paiement à la livraison',
  codNoticeText: 'Payez le livreur après avoir reçu et vérifié votre colis.',
  subtotal: 'Sous-total ({count} {item})',
  item: 'article',
  items: 'articles',
  shipping: 'Livraison',
  shippingPending: 'Calcul en cours…',
  shippingFree: 'GRATUITE',
  total: 'Total à payer',
  fieldRequired: '{field} est obligatoire',
  fieldTooShort: '{field} : au moins {min} caractères',
  fieldPhone: 'Saisissez un numéro valide, ex. 06 12 34 56 78',
  fieldChoose: 'Choisissez une option pour {field}'
};

/** The same card in Arabic. */
const ARABIC_CHECKOUT_LABELS: CheckoutLabels = {
  heading: 'أكمل طلبك',
  loading: 'جاري تحميل النموذج…',
  invalidFields: 'يرجى التحقق من الحقول المظللة والمحاولة من جديد.',
  submitFailed: 'تعذر تسجيل الطلب. حاول من جديد.',
  submitting: 'جاري تسجيل طلبك…',
  codNoticeTitle: 'الدفع عند الاستلام',
  codNoticeText: 'تدفع للموصل بعد استلام طلبك والتحقق منه.',
  subtotal: 'المجموع الفرعي ({count} {item})',
  item: 'قطعة',
  items: 'قطع',
  shipping: 'رسوم التوصيل',
  shippingPending: 'جاري الحساب…',
  shippingFree: 'مجاني',
  total: 'المجموع للدفع',
  fieldRequired: '{field} مطلوب',
  fieldTooShort: '{field}: {min} أحرف على الأقل',
  fieldPhone: 'أدخل رقم هاتف صحيح، مثلا 06 12 34 56 78',
  fieldChoose: 'اختر أحد الخيارات في {field}'
};

/** Every language's card, so a store's choice picks one and nothing guesses. */
const LABELS: Readonly<Record<CheckoutLanguage, CheckoutLabels>> = {
  en: DEFAULT_CHECKOUT_LABELS, fr: FRENCH_CHECKOUT_LABELS, ar: ARABIC_CHECKOUT_LABELS
};

/** The submit button, the reassurance line, and the button a popup form opens from, per language. */
type SubmitText = CodFormDefaultWords;

/** The card's button words in each language, where neither Form style nor a theme set any. */
const LANGUAGE_SUBMIT: Readonly<Record<CheckoutLanguage, SubmitText>> = COD_FORM_DEFAULT_WORDS;

// Picks the card's language: the one the store chose, else Arabic for a right-to-left theme and English otherwise.
export const checkoutLanguage = (language: CheckoutLanguage | null | undefined, direction: CheckoutDirection): CheckoutLanguage =>
  language ?? (direction === 'rtl' ? 'ar' : 'en');

// Answers the default submit wording in the card's language.
export const languageSubmitText = (language: CheckoutLanguage): SubmitText =>
  LANGUAGE_SUBMIT[language];

/** Everything a theme may say in the shared card's place, which is exactly its optional props. */
export type CheckoutText = {
  labels?: Partial<CheckoutLabels>;
  submitLabel?: string;
  secureNote?: string;
};

// Fills every string a theme did not supply with the card's wording in the store's language.
export const resolveCheckoutLabels = (overrides?: Partial<CheckoutLabels>, language: CheckoutLanguage = 'en'): CheckoutLabels => {
  const resolved: CheckoutLabels = { ...LABELS[language] };
  if (!overrides) return resolved;
  for (const key of Object.keys(DEFAULT_CHECKOUT_LABELS) as readonly (keyof CheckoutLabels)[]) {
    const value = overrides[key];
    if (typeof value === 'string' && value.length > 0) resolved[key] = value;
  }
  return resolved;
};

// Prints the subtotal row with the line count folded into whichever wording is in force.
export const subtotalLabel = (labels: CheckoutLabels, quantity: number): string =>
  labels.subtotal
    .replace('{count}', String(quantity))
    .replace('{item}', quantity > 1 ? labels.items : labels.item);

// Hands the shell's own checkout element a theme's wording, so a theme places that element instead of building one.
export const withCheckoutText = (node: ReactNode, text: CheckoutText): ReactNode =>
  (isValidElement<CheckoutText>(node) ? cloneElement(node, text) : node);
